import {collection, doc, onSnapshot} from 'firebase/firestore'
import {saveAs} from 'file-saver'
import {useAuth} from '../../../../modules/auth'
import db from '../../../../../services/firebase'
import {useEffect, useState} from 'react'
import ExcelJS from 'exceljs'
import UpgradePlan from 'app/components/UpgradePlan'
import {showErrorToast, showSuccessToast} from 'app/components/ShowToast'
import ExportButton from 'app/components/ExportButton'
import useAuditContext from 'app/Hooks/useAuditContext'

const TestListToolbar = () => {
  const {currentUser} = useAuth()
  const [tests, setTests] = useState([] as any[])
  const [activeFrameworks, setActiveFrameworks] = useState<any[]>([])
  const [filteredItems, setFilteredItems] = useState<any[]>([])
  const [showModal, setShowModal] = useState(false)
  const {currentAuditContext} = useAuditContext()

  useEffect(() => {
    getActiveFramworks()
  }, [])

  useEffect(() => {
    getTests()
  }, [activeFrameworks])

  const getActiveFramworks = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)

      const testsCollectionRef = collection(tenantRef, 'active_frameworks')

      const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
        const frameworksData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          frameworks: doc.data().frameworks,
        }))
        const frameworks = frameworksData[0]?.frameworks || []
        setActiveFrameworks(frameworks)
      })

      return unsubscribe 
    } catch (error) {
      console.error('Error getting tests:', error)
    }
  }

  useEffect(() => {
    if (activeFrameworks.length > 0) {
      const filteredTests = tests.filter((test) => {
        const testFrameworks = test.framework.split(',').map((fw) => fw.trim())

        return activeFrameworks.some((activeFramework) =>
          testFrameworks.some((testFramework) => testFramework === activeFramework)
        )
      })

      setFilteredItems(filteredTests)
    } else {
      setFilteredItems(tests)
    }
  }, [tests, activeFrameworks])

  const getTests = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      const tenantRef = doc(db, 'tenants', tenantId)
      const testsCollectionRef = collection(tenantRef, 'tests')

      const unsubscribe = onSnapshot(testsCollectionRef, (snapshot) => {
        const testsData = snapshot.docs.map((doc) => ({
          ...doc.data(),
          id: doc.id,
          framework: doc.data().framework,
          status: doc.data().status,
        }))

        setTests(testsData)
      })

      return unsubscribe
    } catch (error) {
      console.log('Error getting tests:', error)
    }
  }

  const stripHtmlTags = (html) => {
    const tmp = document.createElement('DIV')
    tmp.innerHTML = html
    return tmp.textContent || tmp.innerText || ''
  }

  const exportData = async () => {
    if (filteredItems.length > 0) {
      const fields = [
        {key: 'name', header: 'Name', width: 40},
        {key: 'description', header: 'Description', width: 50},
        {key: 'category', header: 'Category', width: 20},
        {key: 'type', header: 'Type', width: 15},
        {key: 'framework', header: 'Framework', width: 30},
        {key: 'status', header: 'Status', width: 15},
        {key: 'owner', header: 'Owner', width: 20},
        {key: 'timeframe', header: 'Timeframe', width: 20},
        {key: 'next_review_date', header: 'Next Review Date', width: 20},
        {key: 'files', header: 'Files', width: 50},
        {key: 'deactivation_reason', header: 'Justification for exclusion', width: 40},
      ]

      const workbook = new ExcelJS.Workbook()
      const worksheet = workbook.addWorksheet('Tests')

      worksheet.columns = fields.map((field) => ({
        header: field.header,
        key: field.key,
        width: field.width,
      }))

      const headerRow = worksheet.getRow(1)
      headerRow.font = {bold: true, color: {argb: 'FFFFFFFF'}, size: 10}
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: {argb: 'FF000000'},
      }
      headerRow.height = 30
      headerRow.alignment = {horizontal: 'center', vertical: 'middle'}
      worksheet.views = [{state: 'frozen', ySplit: 1}]
      const activeFrameworksMap = new Set(activeFrameworks.map((fw) => fw.toLowerCase()))

      worksheet.getCell('J1').note = {
        texts: [{text: 'Click on file links to download directly.'}],
        margins: {insetmode: 'auto'},
      }

      filteredItems.forEach((row) => {
        const rowData = fields.reduce((acc, field) => {
          let value = row[field.key] || row[field.key.toLowerCase()] || ''

          if (field.key === 'description') {
            value = stripHtmlTags(value)
          }

          if (field.key === 'next_review_date' && value) {
            value = value.split('T')[0]
          }

          if (field.key === 'files') {
            acc[field.key] = ''
          } else if (Array.isArray(value)) {
            acc[field.key] = value.join('; ')
          } else if (typeof value === 'object' && value !== null) {
            acc[field.key] = JSON.stringify(value).replace(/"/g, '""')
          } else {
            acc[field.key] = value.toString().replace(/"/g, '""').replace(/\n/g, ' ')
          }

          if (field.key === 'framework') {
            const frameworks = value.split(',').map((fw) => fw.trim().toLowerCase())
            acc[field.key] = frameworks
              .filter((fw) => activeFrameworksMap.has(fw))
              .map((fw) => fw.toUpperCase())
              .join(', ')
          }

          return acc
        }, {})

        const addedRow = worksheet.addRow(rowData)
        addedRow.height = 20
        addedRow.font = {size: 10}

        // Add deactivated row styling
        if (row.status === 'Deactivated') {
          addedRow.eachCell((cell) => {
            cell.fill = {
              type: 'pattern',
              pattern: 'solid',
              fgColor: {argb: 'DDEEFF'},
            }
          })
        }

        const fileCell = addedRow.getCell('files')
        if (row.files && Array.isArray(row.files) && row.files.length > 0) {
          const latestFile = row.files[row.files.length - 1]
          let downloadUrl = latestFile.url

          if (downloadUrl.includes('firebasestorage.googleapis.com')) {
            downloadUrl = `${downloadUrl}&response-content-disposition=attachment;filename=${encodeURIComponent(
              latestFile.name
            )}`
          }

          const escapedName = latestFile.name.replace(/"/g, '""')
          const escapedUrl = downloadUrl.replace(/"/g, '""')

          const formula = `HYPERLINK("${escapedUrl}", "${escapedName}")`
          fileCell.value = {formula: formula}
          fileCell.alignment = {wrapText: true, vertical: 'middle'}
        } else {
          fileCell.value = ''
        }

        const statusCell = addedRow.getCell(6)
        if (statusCell.value === 'Pass') {
          statusCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: {argb: 'D9EAD3'},
          }
        } else if (statusCell.value === 'Fail') {
          statusCell.fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: {argb: 'F4CCCC'},
          }
        }
      })

      const buffer = await workbook.xlsx.writeBuffer()
      const excelBlob = new Blob([buffer], {type: 'application/octet-stream'})
      saveAs(excelBlob, 'tests.xlsx')
      showSuccessToast({message: 'Test data exported successfully'})
    } else {
      showErrorToast({message: 'No data to export'})
    }
  }

  return (
    <div className='d-flex justify-content-end' data-kt-user-table-toolbar='base'>
        {(currentUser?.role !== 'Auditor' && !currentAuditContext) && (
        <ExportButton
          className='me-4'
          onClick={() => (currentUser?.account_type === 'Trial' || currentUser?.account_type === 'Trial-plus' ? setShowModal(true) : exportData())}
        />
       )}
      <UpgradePlan show={showModal} onClose={() => setShowModal(false)} />
    </div>
  )
}

export {TestListToolbar}
